import { useState, useEffect } from 'react';
import {
  Modal,
  Stack,
  Group,
  Text,
  TextInput,
  Textarea,
  Select,
  NumberInput,
  Button,
  SimpleGrid,
  Switch,
  Alert,
  Divider,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { AlertCircle } from 'react-feather';
import type {
  TaskTemplateWithDetails,
  JobTitle,
  TaskAnchorType,
  TaskPriority,
  TemplateType,
  CreateTaskTemplateRequest,
  PhaseWithSubGroups,
} from '../../../../types';
import { taskTemplatesApi } from '../../../../services/api/taskTemplates';
import { TASK_ANCHOR_OPTIONS, TASK_PRIORITY_OPTIONS } from '../../../../config/taskConfig';

const TEMPLATE_TYPE_OPTIONS = [
  { value: 'base', label: 'Base (All Cohorts)' },
  { value: 'group_coaching', label: 'Group Coaching' },
  { value: 'one_on_one', label: '1:1 Coaching' },
  { value: 'workshop', label: 'Workshop' },
];

interface CreateTemplateModalProps {
  opened: boolean;
  onClose: () => void;
  onSuccess: () => void;
  templateType: TemplateType;
  phases: PhaseWithSubGroups[];
  jobTitles: JobTitle[];
  parentTemplates: TaskTemplateWithDetails[];
  defaultPhaseId?: string;
  defaultSubGroupId?: string | null;
}

interface FormData {
  template_type: TemplateType;
  phase_id: string | null;
  sub_group_id: string | null;
  task_title: string;
  task_description: string;
  job_title_id: string | null;
  anchor_type: TaskAnchorType;
  anchor_session_number: number | null;
  days_offset: number;
  default_priority: TaskPriority;
  is_required: boolean;
  is_session_specific: boolean;
  parent_template_id: string | null;
}

export function CreateTemplateModal({
  opened,
  onClose,
  onSuccess,
  templateType,
  phases,
  jobTitles,
  parentTemplates,
  defaultPhaseId,
  defaultSubGroupId,
}: CreateTemplateModalProps) {
  const [formData, setFormData] = useState<FormData>({
    template_type: templateType,
    phase_id: null,
    sub_group_id: null,
    task_title: '',
    task_description: '',
    job_title_id: null,
    anchor_type: 'cohort_start',
    anchor_session_number: null,
    days_offset: 0,
    default_priority: 'medium',
    is_required: true,
    is_session_specific: false,
    parent_template_id: null,
  });
  const [saving, setSaving] = useState(false);

  // Reset form when modal opens with defaults
  useEffect(() => {
    if (opened) {
      setFormData({
        template_type: templateType,
        phase_id: defaultPhaseId || null,
        sub_group_id: defaultSubGroupId || null,
        task_title: '',
        task_description: '',
        job_title_id: null,
        anchor_type: 'cohort_start',
        anchor_session_number: null,
        days_offset: 0,
        default_priority: 'medium',
        is_required: true,
        is_session_specific: false,
        parent_template_id: null,
      });
    }
  }, [opened, templateType, defaultPhaseId, defaultSubGroupId]);

  // Get sub-groups for selected phase
  const availableSubGroups = formData.phase_id
    ? phases.find(p => p.id === formData.phase_id)?.sub_groups || []
    : [];

  // Handle per-session toggle
  const handlePerSessionChange = (checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      is_session_specific: checked,
      // Per-session tasks use 'session' anchor and don't have a specific session number
      anchor_type: checked ? 'session' : 'cohort_start',
      anchor_session_number: null,
      // Per-session tasks typically don't belong to a sub-group
      sub_group_id: checked ? null : prev.sub_group_id,
    }));
  };

  const handleSave = async () => {
    // Validation
    if (!formData.task_title.trim()) {
      notifications.show({
        title: 'Validation Error',
        message: 'Task title is required',
        color: 'red',
      });
      return;
    }

    if (!formData.phase_id) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please select a phase',
        color: 'red',
      });
      return;
    }

    // Per-session tasks should include {session_number} in title
    if (formData.is_session_specific && !formData.task_title.includes('{session_number}')) {
      notifications.show({
        title: 'Validation Warning',
        message: 'Per-session tasks typically include {session_number} in the title for clarity',
        color: 'yellow',
      });
    }

    setSaving(true);
    try {
      const createData: CreateTaskTemplateRequest = {
        template_type: formData.template_type,
        phase_id: formData.phase_id,
        sub_group_id: formData.sub_group_id,
        task_title: formData.task_title,
        task_description: formData.task_description || undefined,
        job_title_id: formData.job_title_id,
        anchor_type: formData.anchor_type,
        anchor_session_number: formData.anchor_session_number || undefined,
        days_offset: formData.days_offset,
        default_priority: formData.default_priority,
        is_required: formData.is_required,
        is_session_specific: formData.is_session_specific,
        parent_template_id: formData.parent_template_id || undefined,
      };

      await taskTemplatesApi.create(createData);
      notifications.show({
        title: 'Success',
        message: 'Task created successfully',
        color: 'green',
      });
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to create task:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to create Task',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600} size="lg">Create Task</Text>}
      size="lg"
    >
      <Stack gap="md">
        {/* Per-Session Task Toggle */}
        <Alert
          icon={<AlertCircle size={16} />}
          color="grape"
          variant="light"
          style={{ display: formData.is_session_specific ? 'flex' : 'none' }}
        >
          Per-session tasks generate one task for each session in a cohort. The task title should include {'{session_number}'} to differentiate them.
        </Alert>

        <Switch
          label="Per-Session Task"
          description="Creates a separate task for each session in the cohort"
          checked={formData.is_session_specific}
          onChange={e => handlePerSessionChange(e.target.checked)}
          color="grape"
        />

        <Divider />

        {/* Template Parent */}
        <Select
          label="Template Parent"
          description="Which cohort types this template applies to"
          data={TEMPLATE_TYPE_OPTIONS}
          value={formData.template_type}
          onChange={value => setFormData(prev => ({ ...prev, template_type: value as TemplateType }))}
          required
        />

        {/* Phase & Sub-Group */}
        <SimpleGrid cols={2}>
          <Select
            label="Phase"
            description="Which phase this task belongs to"
            data={phases.map(p => ({ value: p.id, label: p.name }))}
            value={formData.phase_id}
            onChange={value =>
              setFormData(prev => ({
                ...prev,
                phase_id: value,
                sub_group_id: null, // Reset sub-group when phase changes
              }))
            }
            required
          />
          <Select
            label="Sub-Group"
            description="Grouping within the phase"
            data={availableSubGroups.map(sg => ({ value: sg.id, label: sg.name }))}
            value={formData.sub_group_id}
            onChange={value => setFormData(prev => ({ ...prev, sub_group_id: value }))}
            disabled={!formData.phase_id || formData.is_session_specific}
            clearable
          />
        </SimpleGrid>

        {/* Task Title & Description */}
        <TextInput
          label="Task Title"
          description={formData.is_session_specific ? 'Use {session_number} for the session number' : undefined}
          placeholder={formData.is_session_specific ? 'Prepare Session {session_number} Materials' : 'Enter task title'}
          value={formData.task_title}
          onChange={e => setFormData(prev => ({ ...prev, task_title: e.target.value }))}
          required
        />

        <Textarea
          label="Description"
          placeholder="Optional task description"
          value={formData.task_description}
          onChange={e => setFormData(prev => ({ ...prev, task_description: e.target.value }))}
          minRows={2}
        />

        {/* Default Assignee */}
        <Select
          label="Default Assignee"
          description="Job title that will be assigned this task"
          placeholder="Select job title"
          data={jobTitles.map(jt => ({ value: jt.id, label: jt.name }))}
          value={formData.job_title_id}
          onChange={value => setFormData(prev => ({ ...prev, job_title_id: value }))}
          clearable
          searchable
        />

        {/* Parent Task (Subtask) */}
        <Select
          label="Parent Task"
          description="Make this a subtask of another task"
          placeholder="None (standalone task)"
          data={parentTemplates
            .filter(t => !t.parent_template_id) // Only show parent-level templates
            .map(t => ({ value: t.id, label: t.task_title }))}
          value={formData.parent_template_id}
          onChange={value => setFormData(prev => ({ ...prev, parent_template_id: value }))}
          clearable
          searchable
        />

        <Divider label="Due Date Settings" labelPosition="left" />

        {/* Anchor Type & Session Number */}
        <SimpleGrid cols={formData.anchor_type === 'session' && !formData.is_session_specific ? 2 : 1}>
          <Select
            label="Due Date Anchor"
            description="What event determines the due date"
            data={TASK_ANCHOR_OPTIONS}
            value={formData.anchor_type}
            onChange={value =>
              setFormData(prev => ({
                ...prev,
                anchor_type: value as TaskAnchorType,
                anchor_session_number: value === 'session' ? prev.anchor_session_number : null,
              }))
            }
            required
            disabled={formData.is_session_specific}
          />
          {formData.anchor_type === 'session' && !formData.is_session_specific && (
            <NumberInput
              label="Session Number"
              description="Which session to anchor to"
              value={formData.anchor_session_number || undefined}
              onChange={value =>
                setFormData(prev => ({
                  ...prev,
                  anchor_session_number: value as number | null,
                }))
              }
              min={1}
            />
          )}
        </SimpleGrid>

        {/* Days Offset */}
        <NumberInput
          label="Days Offset"
          description="Days before (-) or after (+) the anchor date"
          value={formData.days_offset}
          onChange={value =>
            setFormData(prev => ({ ...prev, days_offset: (value as number) || 0 }))
          }
        />

        <Divider label="Other Settings" labelPosition="left" />

        {/* Priority & Required */}
        <SimpleGrid cols={2}>
          <Select
            label="Default Priority"
            data={TASK_PRIORITY_OPTIONS}
            value={formData.default_priority}
            onChange={value =>
              setFormData(prev => ({ ...prev, default_priority: value as TaskPriority }))
            }
            required
          />
          <Select
            label="Required"
            data={[
              { value: 'true', label: 'Yes - Required' },
              { value: 'false', label: 'No - Optional' },
            ]}
            value={formData.is_required ? 'true' : 'false'}
            onChange={value => setFormData(prev => ({ ...prev, is_required: value === 'true' }))}
          />
        </SimpleGrid>

        {/* Actions */}
        <Group justify="flex-end" className="modal-footer">
          <Button variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={saving}>
            Create Task
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default CreateTemplateModal;
